import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { FormattedMessage } from "react-intl";
import { LuChevronLeft, LuChevronRight, LuNotebookPen } from "react-icons/lu";
import { useLocale } from "@/contexts/IntlContext";
import { useAuthStore } from "@/lib/stores/authStore";
import { Button } from "@/components/ui/button";
import { ExerciseLayout } from "@/components/ui/exercise-layout";
import { ProfileHeader } from "@/components/ProfileHeader";
import { ProfileCard } from "@/components/ProfileCard";
import { ProfileInfoRow } from "@/components/ProfileInfoRow";
import { useTrainerAuthRedirect } from "@/lib/hooks/useTrainerAuthRedirect";

export const Route = createFileRoute("/trainer/profile")({
  component: TrainerProfile,
  head: () => ({ meta: [{ title: "Account - HAMD" }] }),
});

function TrainerProfile() {
  const navigate = useNavigate();
  const { locale } = useLocale();
  const isRTL = locale === "ar";
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const isTrainer = user?.accountType === "TRAINER";
  const { setAuthOrigin } = useTrainerAuthRedirect();

  const handleLogout = async () => {
    await logout();
    navigate({ to: "/trainer/courses" });
  };

  if (!isTrainer) {
    return (
      <div className="px-6 py-8 space-y-6">
        <ProfileHeader
          title={<FormattedMessage id="trainer.profile.guestTitle" />}
          subtitle={<FormattedMessage id="trainer.profile.guestSubtitle" />}
        />

        <ProfileCard className="p-4 space-y-3">
          <p className="text-small text-dark-gray">
            <FormattedMessage id="trainer.profile.guestNotice" />
          </p>
          <Link
            to="/trainer/auth"
            onClick={() => setAuthOrigin(null)}
            className="block"
          >
            <Button className="w-full">
              <FormattedMessage id="trainer.auth.registerOrSignIn" />
            </Button>
          </Link>
        </ProfileCard>
      </div>
    );
  }

  return (
    <ExerciseLayout
      contentClassName="space-y-5 py-6"
      actions={
        <Button variant="secondary" className="w-full" onClick={handleLogout}>
          <FormattedMessage id="profile.logout" />
        </Button>
      }
    >
      <ProfileHeader
        name={user?.nickname}
        subtitle={<FormattedMessage id="trainer.profile.trainerBadge" />}
      />

      {user?.email && (
        <ProfileCard>
          <ProfileInfoRow
            label={<FormattedMessage id="trainer.profile.recoveryEmail" />}
            value={user.email}
          />
        </ProfileCard>
      )}

      <ProfileCard>
        <Link
          to="/trainer/notes"
          className="flex items-center justify-between px-4 py-3"
        >
          <span className="flex items-center gap-2 text-small font-medium text-dark-gray">
            <LuNotebookPen size={18} className="text-primary" />
            <FormattedMessage id="trainer.notes.title" />
          </span>
          {isRTL ? (
            <LuChevronLeft size={18} className="text-gray" />
          ) : (
            <LuChevronRight size={18} className="text-gray" />
          )}
        </Link>
      </ProfileCard>
    </ExerciseLayout>
  );
}
